import { useRef, useEffect, useCallback, useState } from "react";
import { useBarcodeScanner } from "./_components/InventoryCheckTab";


interface BarcodeInputProps {
    placeholder?: string;
    onVerified: (barcode: string) => void;
    disabled?: boolean;
}

export const BarcodeInput: React.FC<BarcodeInputProps> = ({
    placeholder = "Scan / paste barcode",
    onVerified,
    disabled = false,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState("");

    useBarcodeScanner(
        useCallback(
            (barcode) => {
                if (disabled) return;

                // Show the code in the field for a split-second (nice UI)
                setValue(barcode);
                onVerified(barcode);

                // Reset + re-focus for the next scan
                setTimeout(() => {
                    setValue("");
                    inputRef.current?.focus();
                }, 0);
            },
            [disabled, onVerified]
        )
    );

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        const pasted = (e.clipboardData.getData("text") || "").trim();
        if (!pasted) return;

        e.preventDefault(); // stop normal paste
        setValue(pasted);
        onVerified(pasted);

        // clear after verify
        setTimeout(() => {
            setValue("");
            inputRef.current?.focus();
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        if (e.key === "Enter" && value.trim()) {
            onVerified(value.trim());

            // clear + focus for next entry
            setTimeout(() => {
                setValue("");
                inputRef.current?.focus();
            }, 0);
        }

        // Escape → just clear
        if (e.key === "Escape") {
            setValue("");
            inputRef.current?.blur();
        }
    };

    useEffect(() => {
        if (!disabled) {
            const timer = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(timer);
        }
    }, [disabled]);

    return (
        <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-[200px] rounded-full py-[6px] px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E]"
        />
    );
};